import React, { useState, useEffect, useRef } from 'react';
import '../assets/css/psearch.css';
import apiClient from '../config/config';
import { supabase } from '../supabaseClient';

function TryOn({ productImage, onClose, user }) {
  const [step, setStep] = useState(0);
  const [userImage, setUserImage] = useState(null);
  const [userPreview, setUserPreview] = useState(null);
  const [resultImage, setResultImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingLook, setSavingLook] = useState(false);
  const [lookSaved, setLookSaved] = useState(false);
  const [error, setError] = useState(null);
  const [randomFact, setRandomFact] = useState('');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [usageRemaining, setUsageRemaining] = useState(5);
  const [purchasedCredits, setPurchasedCredits] = useState(0);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [selectedTier, setSelectedTier] = useState('pro');
  const inputRef = useRef();

  const pricingTiers = [
    // {
    //   id: 'trial',
    //   name: 'Trial',
    //   description: 'Try it out with a single virtual try-on',
    //   credits: 1,
    //   price: 1.00,
    //   isMonthly: false,
    //   features: ['1 virtual try-on', 'Save to your Fashion Board']
    // },
    {
      id: 'starter',
      name: 'Starter',
      description: 'Perfect for individuals exploring Kashmiri fashion and casual users',
      credits: 10,
      price: 4.99,
      isMonthly: false,
      features: ['10 additional virtual try-ons', 'Save unlimited looks to your Fashion Board']
    },
    {
      id: 'pro',
      name: 'Pro',
      description: 'Ideal for regular shoppers',
      credits: 50,
      price: 14.99,
      isMonthly: false,
      popular: true,
      features: ['Everything in Starter', '50 additional virtual try-ons', 'Save unlimited looks to your Fashion Board']
    },
    {
      id: 'proplus',
      name: 'Pro+',
      description: 'Tailored for power users, influencers, fashion first people.',
      credits: 999,
      price: 9.99,
      isMonthly: true,
      features: ['All Pro features', 'Unlimited virtual try-ons', 'Save unlimited looks to your Fashion Board']
    }
  ];

  const getRandomFact = async () => {
    try {
      // Try fetching from Wikipedia API about Kashmiri fashion/textiles
      const topics = [
        'Pashmina',
        'Kashmiri_handicrafts',
        'Embroidery',
        'Kashmir_shawl',
        'Kashmiri_art'
      ];
      const randomTopic = topics[Math.floor(Math.random() * topics.length)];
      
      const response = await fetch(
        `https://en.wikipedia.org/api/rest_v1/page/summary/${randomTopic}`
      );
      
      if (response.ok) {
        const data = await response.json();
        // Extract a sentence from the description
        const sentences = data.extract.split('. ');
        const factSentence = sentences[Math.floor(Math.random() * Math.min(sentences.length, 3))];
        setRandomFact(factSentence + (factSentence.endsWith('.') ? '' : '.'));
      } else {
        // Fallback to a default message
        setRandomFact("Kashmir is renowned for its exquisite textiles and intricate embroidery traditions.");
      }
    } catch (err) {
      console.error('Error fetching fact:', err);
      // Fallback fact
      setRandomFact("Kashmiri craftsmanship represents centuries of artistic excellence and cultural heritage.");
    }
  };

  const handleSaveLook = async () => {
    if (!user?.id) return alert('Please sign in to save your look.');
    if (!resultImage) return;

    setSavingLook(true);
    setError(null);

    try {
      const { error: insertError } = await supabase
        .from('saved_looks')
        .insert({
          user_id: user.id,
          image: resultImage,
          product_image: productImage,
        });

      if (insertError) {
        setError(insertError.message || 'Failed to save look.');
        return;
      }

      setLookSaved(true);
      setTimeout(() => setLookSaved(false), 3000);
    } catch (e) {
      setError(e.message || 'Failed to save look.');
    } finally {
      setSavingLook(false);
    }
  };

  /* Lock body scroll */
  
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    if (user?.id) {
      checkUsageLimit();
    }
    return () => (document.body.style.overflow = 'auto');
  }, [user]);

  const checkUsageLimit = async () => {
    if (!user?.id) return;
    
    try {
      const res = await apiClient.get('/tryon/check-usage/', {
        params: { user_id: user.id }
      });
      setUsageRemaining(res.data.remaining);
      if (res.data.limit_reached) {
        setShowUpgradeModal(true);
      }
      if (res.data.purchased_credits !== undefined) {
        setPurchasedCredits(res.data.purchased_credits);
      }
    } catch (err) {
      console.error('Error checking usage limit:', err);
    }
  };

  /* Preview uploaded image */
  useEffect(() => {
    if (!userImage) return;
    const url = URL.createObjectURL(userImage);
    setUserPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [userImage]);

  const urlToFile = async (url) => {
    try {
      // Use the backend proxy to fetch the image and avoid CORS issues
      const proxyUrl = `/proxy-image/?url=${encodeURIComponent(url)}`;
      const res = await apiClient.get(proxyUrl, { responseType: 'blob' });
      return new File([res.data], 'fit.jpg', { type: res.data.type || 'image/jpeg' });
    } catch (error) {
      console.error('Error fetching image via proxy:', error);
      throw error;
    }
  };

  const handleGenerate = async () => {
    if (!userImage) return alert('Upload your photo first.');
    if (!user?.id) return alert('Please sign in to use this feature.');

    // Check usage limit before generating (GET request - no deduction yet)
    try {
      const usageCheck = await apiClient.get('/tryon/track-usage/', {
        params: { user_id: user.id }
      });
      setUsageRemaining(usageCheck.data.remaining);
      
      if (usageCheck.data.limit_reached) {
        setShowUpgradeModal(true);
        return;
      }
      if (usageCheck.data.purchased_credits !== undefined) {
        setPurchasedCredits(usageCheck.data.purchased_credits);
      }
    } catch (err) {
      if (err.response?.status === 429) {
        setShowUpgradeModal(true);
        return;
      }
      console.error('Error checking usage:', err);
    }

    setLoading(true);
    getRandomFact();
    setError(null);

    try {
      const formData = new FormData();
      formData.append('fitImg', await urlToFile(productImage));
      formData.append('userImgs', userImage);

      const res = await fetch('https://zyora-szo7.vercel.app/api/generate-look', {
        method: 'POST',
        body: formData,
        // credentials: 'include',
      });

      const data = await res.json();
      if (res.ok && data?.image) {
        setResultImage(`data:image/png;base64,${data.image}`);
        setStep(1);
        
        // Only deduct credit AFTER successful generation
        try {
          const deductResult = await apiClient.post('/tryon/track-usage/', {
            user_id: user.id,
            user_email: user.email
          });
          setUsageRemaining(deductResult.data.remaining);
          if (deductResult.data.purchased_credits !== undefined) {
            setPurchasedCredits(deductResult.data.purchased_credits);
          }
        } catch (deductErr) {
          console.error('Error deducting credit:', deductErr);
          // Don't show error to user - generation succeeded, credit tracking is secondary
        }
      } else {
        // Generation failed - NO credit deducted
        setError(data?.error || 'Failed to generate look.');
      }
    } catch (err) {
      // Generation failed - NO credit deducted
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async () => {
    if (!user?.id) {
      alert('Please sign in to upgrade.');
      return;
    }

    const tier = pricingTiers.find(t => t.id === selectedTier);
    if (!tier) return;

    setIsProcessingPayment(true);
    try {
      const baseUrl = window.location.origin;
      const response = await apiClient.post('/stripe/create-checkout/', {
        user_id: user.id,
        user_email: user.email,
        success_url: `${baseUrl}/payment-success`,
        cancel_url: `${baseUrl}/payment-cancelled`,
        tier_id: tier.id,
        credits: tier.credits,
        price: Math.round(tier.price * 100),
        is_subscription: tier.isMonthly
      });

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (err) {
      console.error('Error creating checkout session:', err);
      alert('Failed to start payment process. Please try again.');
    } finally {
      setIsProcessingPayment(false);
    }
  };

  return (
    <div className="tryon-overlay">
      {/* UPGRADE MODAL */}
      {showUpgradeModal && (
        <div className="pricing-overlay">
          <div className="pricing-modal">
            <div className="pricing-header">
              <h1>Unlock More Try-Ons!</h1>
              <p>You've used all your free virtual try-ons</p>
            </div>

            <h2 className="pricing-title">Pricing Plans</h2>

            <div className="pricing-cards">
              {pricingTiers.map((tier) => (
                <div 
                  key={tier.id}
                  className={`pricing-card ${selectedTier === tier.id ? 'selected' : ''}`}
                  onClick={() => setSelectedTier(tier.id)}
                >
                  {/* {tier.popular && <span className="popular-tag">Top Pick</span>} */}
                  <div className="card-header">
                    <h3 className="tier-name">{tier.name}</h3>
                    <span className="tier-icon">
                      {tier.id === 'starter' && (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                        </svg>
                      )}
                      {tier.id === 'pro' && (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                        </svg>
                      )}
                      {tier.id === 'proplus' && (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5zm14 3c0 .55-.45 1-1 1H6c-.55 0-1-.45-1-1v-1h14v1z"/>
                        </svg>
                      )}
                    </span>
                  </div>
                  <p className="tier-description">{tier.description}</p>
                  <ul className="tier-features">
                    {tier.features.map((feature, idx) => (
                      <li key={idx}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <div className="tier-price">
                    <span className="price">$ {tier.price.toFixed(2)}</span>
                    {tier.isMonthly && <span className="price-period">/mo</span>}
                  </div>
                </div>
              ))}
            </div>

            <div className="pricing-actions">
              <button 
                className="upgrade-now-btn"
                onClick={handleUpgrade}
                disabled={isProcessingPayment}
              >
                {isProcessingPayment ? 'Processing...' : 'Upgrade Now'}
              </button>
              <button className="maybe-later-btn" onClick={onClose}>
                Maybe Later
              </button>
            </div>

            <p className="secure-note">Secure payment powered by Stripe</p>
          </div>
        </div>
      )}

      <div className="tryon-sidebar">
        {/* HEADER */}
        <header className="tryon-header text-center">
          <div className="tryon-header-content">
            
            <div>
              <h4 className="tryon-header-title">Zyora</h4>
              <p className="tryon-header-subtitle">Virtual Try-On</p>
            </div>
          </div>
          <button className="tryon-close-btn" onClick={onClose}>✕</button>
        </header>
        
        {/* USAGE COUNTER */}
        {/* {usageRemaining > 0 && (
          <div className="usage-counter">
            <span className="usage-text">
              {usageRemaining} {usageRemaining === 1 ? 'try' : 'tries'} remaining
            </span>
          </div>
        )}  */}

        {/* CONTENT */}
        <div className="tryon-content">
          {/* LOADING MODAL */}
          {loading && (
            <div className="tryon-loading-modal">
              <div className="loading-spinner"></div>
              <h3>Generating Your Look...</h3>
              <div className="fashion-fact">
                <p className="fact-label">Did You Know?</p>
                <p className="fact-text">{randomFact}</p>
              </div>
            </div>
          )}

          {step === 0 && (
            <div className="tryon-step1">
              {/* PRODUCT IMAGE - Selected Image */}
              <div className="tryon-section">
                <p>Selected Image</p>
                <img
                  src={productImage}
                  alt="Product"
                  className="product-image"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = '/default-image.avif';
                  }}
                />
              </div>

              {/* UPLOAD CARD */}
              <div className="tryon-upload-section">
                <p>Upload Image</p>
                <div
                  className="upload-card"
                  onClick={() => inputRef.current.click()}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (e.dataTransfer.files?.length) {
                      setUserImage(e.dataTransfer.files[0]);
                    }
                  }}
                  onDragOver={(e) => e.preventDefault()}
                >
                  {!userImage ? (
                    <div className="upload-placeholder">
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="3" width="18" height="18" rx="2" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <path d="M21 15l-5-5L5 21" />
                      </svg>
                      <p>Drag & drop your photo or upload from device</p>
                    </div>
                  ) : (
                    <div className="upload-preview">
                      <img
                        src={userPreview}
                        alt="User"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = '/default-avatar.jpg';
                        }}
                      />
                      <button
                        className="remove-upload"
                        onClick={(e) => {
                          e.stopPropagation();
                          setUserImage(null);
                          setUserPreview(null);
                          // Reset the file input so it can accept new uploads
                          if (inputRef.current) {
                            inputRef.current.value = '';
                          }
                        }}
                      >
                        ×
                      </button>
                    </div>
                  )}

                  <input
                    ref={inputRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) =>
                      e.target.files?.length &&
                      setUserImage(e.target.files[0])
                    }
                  />
                </div>
              </div>

              {/* CTA */}
              <button
                className="tryon-nav-btn"
                disabled={!userImage || loading}
                onClick={handleGenerate}
              >
                {loading ? 'Generating...' : 'Generate Look'}
              </button>
            </div>
          )}

          {step === 1 && (
            <div className="tryon-step2">
              {/* GENERATED LOOK - Result Image */}
              <div className="tryon-section">
                <p>Generated Look</p>
                {resultImage && (
                  <img
                    src={resultImage}
                    alt="Generated Look"
                    className="result-image"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = '/default-image.avif';
                    }}
                  />
                )}
              </div>

              {/* Save and Go Back Buttons */}
              <div className="tryon-button-group">
                <button
                  className="tryon-nav-btn go-back-btn"
                  onClick={() => {
                    setStep(0);
                    setResultImage(null);
                  }}
                >
                  ← Go Back
                </button>
                <button
                  className="tryon-nav-btn save-btn"
                  onClick={handleSaveLook}
                  disabled={savingLook || !resultImage}
                >
                  {savingLook ? 'Saving...' : 'Save The Look'}
                </button>
              </div>
            </div>
          )}

          {error && <p className="tryon-error">{error}</p>}

          {/* Success Modal */}
          {lookSaved && (
            <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }}>
              <div className="modal-content" style={{ background: '#fff', padding: '32px 40px', borderRadius: '25px', textAlign: 'center', maxWidth: '400px', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#27ae60" strokeWidth="2" style={{ marginBottom: '16px' }}>
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                <p style={{ fontSize: '18px', color: '#1a1a1a', marginBottom: '20px' }}>Look saved to your Fashion Board!</p>
                <button 
                  onClick={() => setLookSaved(false)} 
                  style={{ padding: '10px 24px', background: '#1a1a1a', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}
                >
                  OK
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TryOn;
